<template>
    <div class="wf-field">
        <label class="wf-label">
            {{ label }} <span v-if="isRequired" class="wf-required">*</span>
        </label>
        <p v-if="instruction" class="wf-instruction">{{ instruction }}</p>

        <div class="wf-geo-row">
            <div class="wf-geo-input-wrap">
                <i
                    class="wf-geo-icon"
                    :class="busy ? 'fal fa-spinner-third fa-spin' : 'fal fa-location-arrow'"
                ></i>
                <input
                    type="text"
                    class="wf-input wf-geo-input"
                    :class="{ 'wf-input--error': error }"
                    :value="displayValue"
                    :placeholder="busy ? $t('wizard.geolocation_pending') : $t('wizard.geolocation_empty')"
                    readonly
                />
            </div>
            <button
                type="button"
                class="wf-geo-btn"
                :disabled="busy"
                @click="locate"
            >
                <i class="fal fa-sync-alt"></i>
                <span class="wf-geo-btn-text">{{ $t('wizard.geolocation_retry') }}</span>
            </button>
        </div>

        <p v-if="geoError" class="wf-geo-error">
            <i class="fal fa-exclamation-triangle mr-2"></i>{{ geoError }}
        </p>

        <p v-if="help" class="wf-help">{{ help }}</p>
    </div>
</template>

<script>
import FieldMixin from './FieldMixin'
import LocationService from '@/services/LocationService'

// BANOC answers a coordinate with a cell whose size depends on this level —
// same scale as the radius picker of the location field (155 ≈ 5 m).
const DEFAULT_BANOC_LEVEL = 155

export default {
    name: 'GeolocationAutomaticField',
    mixins: [FieldMixin],
    data() {
        return {
            busy: false,
            geoError: ''
        }
    },
    computed: {
        config() {
            const raw = this.attribute.config
            if (!raw) return {}
            if (typeof raw === 'string') {
                try {
                    return JSON.parse(raw)
                } catch (e) {
                    return {}
                }
            }
            return raw
        },
        provider() {
            return this.config.provider || 'osm'
        },
        isBanoc() {
            return this.provider === 'banoc'
        },
        banocLevel() {
            return this.config.level || DEFAULT_BANOC_LEVEL
        },
        displayValue() {
            if (!this.value) return ''
            return this.value.address
                || (this.value.lat ? `${this.value.lat}, ${this.value.lon}` : '')
        }
    },
    mounted() {
        // An answer already captured (edit, back-navigation) is kept as-is:
        // re-locating would silently overwrite the submitted position.
        if (!this.value || !this.value.lat) this.locate()
    },
    methods: {
        locate() {
            if (!navigator.geolocation) {
                this.geoError = this.$t('wizard.geolocation_unsupported')
                return
            }
            this.busy = true
            this.geoError = ''
            navigator.geolocation.getCurrentPosition(
                (pos) => this.resolveAddress(pos.coords.latitude, pos.coords.longitude),
                (err) => {
                    this.busy = false
                    // 1 = PERMISSION_DENIED — the only case the citizen can fix.
                    this.geoError = err && err.code === 1
                        ? this.$t('wizard.geolocation_denied')
                        : this.$t('wizard.geolocation_unavailable')
                },
                { enableHighAccuracy: true, timeout: 10000 }
            )
        },

        // Same payload as the location field (type 6) — { lat, lon, address } —
        // so the answer lands on the map view and renders like any location.
        // The reverse geocoding only fills `address`, kept empty on failure
        // rather than losing the coordinates.
        async resolveAddress(lat, lon) {
            let address = ''
            try {
                const service = this.buildService()
                if (service) {
                    const params = { lat, lon }
                    if (this.isBanoc) params.level = this.banocLevel
                    address = (await service.reverseSearch(params)) || ''
                }
            } catch (e) {
                address = ''
            }

            const payload = { lat, lon, address }
            // BANOC resolves to a cell, not a point: the size it was read at
            // travels with the answer, as it does for a BANOC location field.
            if (this.isBanoc) payload.radius = this.banocLevel

            this.emitValue(payload)
            this.busy = false
        },

        buildService() {
            const companyId = this.$store.state.auth && this.$store.state.auth.company
                ? this.$store.state.auth.company.id
                : null
            try {
                return new LocationService(
                    { mapProvider: this.provider, restriction: { country: '', city: '' } },
                    companyId
                )
            } catch (e) {
                console.warn('LocationService init failed, provider:', this.provider, e)
                return null
            }
        }
    }
}
</script>

<style scoped>
.wf-geo-row {
    display: flex;
    align-items: stretch;
    gap: 8px;
}

.wf-geo-input-wrap {
    position: relative;
    flex: 1 1 auto;
    min-width: 0;
}

.wf-geo-icon {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    color: #0891b2;
    font-size: 0.9rem;
    pointer-events: none;
}

.wf-geo-input {
    width: 100%;
    padding-left: 34px;
    background: #f8fafc;
    cursor: default;
}

.wf-geo-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex: 0 0 auto;
    padding: 0 14px;
    border: none;
    border-radius: 8px;
    background: #0891b2;
    color: #fff;
    font-size: 0.82rem;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.15s ease;
}

.wf-geo-btn i {
    color: #fff;
}

.wf-geo-btn:hover:not(:disabled) {
    background: #0e7490;
}

.wf-geo-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.wf-geo-error {
    margin: 6px 0 0;
    font-size: 0.78rem;
    color: #dc2626;
}

@media (max-width: 640px) {
    .wf-geo-row {
        flex-wrap: wrap;
    }

    .wf-geo-btn {
        width: 100%;
        justify-content: center;
        padding: 10px 14px;
    }
}
</style>
